/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from '@/components/ui/Badge/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { createColumnHelper, Table } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { PropsWithChildren, useState } from 'react';
import { useForm } from 'react-hook-form';

type Todo = {
  id: number;
  title: string;
  done: boolean;
  due: Date;
  labels: string[];
};

// Mock todo data
const mockTodos: Todo[] = [
  {
    id: 1,
    title: 'Buy groceries',
    done: false,
    due: new Date('2024-06-10'),
    labels: ['shopping', 'errands'],
  },
  {
    id: 2,
    title: 'Finish project report',
    done: true,
    due: new Date('2024-06-05'),
    labels: ['work'],
  },
  {
    id: 3,
    title: 'Call plumber',
    done: false,
    due: new Date('2024-06-12'),
    labels: ['home'],
  },
  {
    id: 4,
    title: 'Read a book',
    done: false,
    due: new Date('2024-06-15'),
    labels: ['leisure'],
  },
  {
    id: 5,
    title: 'Exercise',
    done: false,
    due: new Date('2024-06-08'),
    labels: ['health', 'fitness'],
  },
  {
    id: 6,
    title: 'Pay bills',
    done: true,
    due: new Date('2024-06-03'),
    labels: ['finance', 'important'],
  },
  {
    id: 7,
    title: 'Plan vacation',
    done: false,
    due: new Date('2024-06-20'),
    labels: ['travel', 'leisure'],
  },
  {
    id: 8,
    title: 'Fix car',
    done: false,
    due: new Date('2024-06-14'),
    labels: ['maintenance', 'transport'],
  },
];

type Val = Todo[keyof Todo];

const h = createColumnHelper<Todo>();
const columns = [
  h.accessor('id', {}),
  h.accessor('done', {}),
  h.accessor('title', {
    cell: (props) => (
      <span className={cn(props.row.original.done && 'line-through')}>
        {props.getValue()}
      </span>
    ),
  }),
  h.accessor('due', { cell: (props) => props.getValue().toLocaleDateString() }),
  h.accessor('labels', {
    cell: (props) => (
      <div className="flex gap-1">
        {props.getValue().map((label) => (
          <Badge key={label}>{label}</Badge>
        ))}
      </div>
    ),
  }),
];

type BulkEditFormData = {
  done?: boolean;
  removeLabels: string[];
  newLabel: string;
};

function BulkEditForm({
  todos,
  setTodos,
  table,
  onClose,
}: {
  todos: Todo[];
  setTodos: (todos: Todo[]) => void;
  table: Table<Todo>;
  onClose: () => void;
}) {
  const form = useForm<BulkEditFormData>({
    defaultValues: {
      removeLabels: [],
      newLabel: '',
    },
  });

  const onSubmit = (data: BulkEditFormData) => {
    const ids = table.getSelectedRowModel().rows.map((r) => r.original.id);
    setTodos(
      todos.map((todo) => {
        if (ids.includes(todo.id)) {
          const newTodo = { ...todo };
          if (typeof data.done !== 'undefined') {
            newTodo.done = data.done;
          }
          if (data.removeLabels.length > 0) {
            newTodo.labels = newTodo.labels.filter(
              (label) => !data.removeLabels.includes(label),
            );
          }
          if (data.newLabel) {
            newTodo.labels.push(data.newLabel);
          }
          return newTodo;
        }
        return todo;
      }),
    );

    onClose();
  };

  const selectedRows = table.getSelectedRowModel().rows;
  const uniqueLabels = Array.from(
    new Set(
      selectedRows.flatMap((row: any) => row.original.labels as string[]),
    ),
  ) as string[];

  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [taskStatus, setTaskStatus] = useState<string>('no-change');

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
      <div className="space-y-2">
        <h3 className="font-medium">Task Status</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              {taskStatus === 'no-change'
                ? 'No change'
                : taskStatus === 'done'
                  ? 'Mark as Done'
                  : 'Mark as Not Done'}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-full">
            <DropdownMenuItem
              onClick={() => {
                setTaskStatus('no-change');
                form.setValue('done', undefined);
              }}
            >
              No change
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setTaskStatus('done');
                form.setValue('done', true);
              }}
            >
              Mark as Done
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setTaskStatus('not-done');
                form.setValue('done', false);
              }}
            >
              Mark as Not Done
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-2">
        <h3 className="font-medium">Remove Labels</h3>
        <div className="flex flex-wrap gap-2">
          {uniqueLabels.map((label) => (
            <Badge
              key={label}
              variant={selectedLabels.includes(label) ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => {
                setSelectedLabels((prev) => {
                  const newSelection = prev.includes(label)
                    ? prev.filter((l) => l !== label)
                    : [...prev, label];
                  form.setValue('removeLabels', newSelection);
                  return newSelection;
                });
              }}
            >
              {label}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="font-medium">Add Label</h3>
        <div className="flex gap-2">
          <Input placeholder="New label" {...form.register('newLabel')} />
        </div>
      </div>

      <Button type="submit" className="w-full">
        Apply Changes
      </Button>
    </form>
  );
}

function TodoTable({
  setTodos,
  ...props
}: {
  setTodos: (todos: Todo[]) => void;
} & any) {
  return (
    <DataTable<Todo, Val>
      createUriPath={(todo) => `/todos/${todo.id}`}
      renderBulkEdit={(table, close) => (
        <BulkEditForm
          todos={props.data}
          setTodos={setTodos}
          table={table}
          onClose={close}
        />
      )}
      columns={columns}
      columnVisibility={{ title: true, done: true }}
      {...props}
    />
  );
}

export default function TableDemo({ children }: PropsWithChildren) {
  const [todos, setTodos] = useState(mockTodos);
  // useEffect(() => {
  //   console.log(todos);
  // }, [todos]);
  const tableConfig = /* useTableConfig("todo"); */ {}; // { rowOrdering, columnLayout, rowFiltering, onRowOrderingChanged, ... }
  const resource = /* useTodos();  */ { data: todos, isLoading: false };

  return (
    <>
      {/* <Button onClick={() => setTodos(mockTodos)}>Reset</Button> */}
      <TodoTable setTodos={setTodos} {...resource} {...tableConfig} />
      {/* <Button
        onClick={() => {
          const greetings = [
            "Hello",
            "Hola",
            "Bonjour",
            "Ciao",
            "こんにちは",
            "안녕하세요",
            "你好",
            "नमस्ते",
            "مرحبا",
            "Привет",
          ];
          const randomGreeting =
            greetings[Math.floor(Math.random() * greetings.length)];
          toast(randomGreeting);
        }}
      >
        Say Hi
      </Button> */}
      {children}
    </>
  );
}
